<template>
    <modal v-if="getSelectedEvent" v-on:closemodal="cancelEdit" @close="cancelEdit" dialog="modal-lg" class="eventEdit-modal view dark-top">
        <div slot="title" class="fs-large mt-4 mb-3 text-center">
            {{ selectedEventTitle }}
        </div>

        <div slot="body">
            <div class="event-changed-banner bg-color-1 lightest text-center"
                :class="{ 'active': hasSameEventUpdatedOnAnotherTab }">
                This event was changed.
                <span class="bold" @click="reloadSelectedEvent"
                    style="text-decoration: underline; cursor: pointer;">Refresh</span>
                to see the latest changes.
            </div>
            <div class="eventEditBody d-flex position-relative">
                <div v-if="isloadingEvent" class="loading-overlay d-flex justify-content-center align-items-center">
                    <div class="text-center">
                        <loader></loader>
                        <span class="d-block mt-2 bold">Loading Event</span>
                    </div>
                </div>
                <div class="col-md-3 bg-lighter darker font-weight-600 px-0 flexGrow1">
                    <ul class="pt-1">
                        <template v-for="tabItem in tabItems">
                            <li :key="tabItem.area" class="py-3 pl-3 pointer" :class="{ 'bg-light': tabItem.selected }"
                                @click="selectTabItem(tabItem)" v-if="tabItem.visible == true">
                                {{ tabItem.label }}
                                <span v-if="hasTabError(tabItem)" class="tab-error-dot"></span>
                            </li>
                        </template>
                    </ul>
                </div>
                <div class="col-md-9 overflow-test">
                    <div :is="activeArea" :validations="$v" class="pb-4"></div>
                </div>
            </div>
        </div>

        <div slot="footer">
            <footerButtons v-on:cancelEdit="cancelEdit" :validations="$v" v-bind="{ hasSameEventUpdatedOnAnotherTab, hasEventChangedOnOtherTab, isloadingEvent }"></footerButtons>
        </div>
    </modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { required } from 'vuelidate/lib/validators'

import isEqual from 'lodash/isEqual'
import Modal from './Modal'
import Loader from '@/tools/loaders/loader1'
import $ from 'jquery'
import { getEventEditChannel } from '@/utils/eventEditChannel'
import { eventHub } from '../../main'

import areaDetails from './EventEdit/areaDetails'
import areaCommunication from './EventEdit/areaCommunication'
import areaCapacity from './EventEdit/areaCapacity'
import areaResources from './EventEdit/areaResources'
import areaRulesPax from './EventEdit/areaRulesPax'
import areaSalesPermissions from './EventEdit/areaSalesPermissions'
import areaBookingQuestion from './EventEdit/areaBookingQuestion'
import areaPrices from './EventEdit/areaPrices'
import areaTransports from './EventEdit/areaTransports'
import areaCutoff from './EventEdit/areaCutoff'
import areaRules from './EventEdit/areaRules'
import footerButtons from './EventEdit/footerButtons'
import { eventFormStateHelper } from '@/store/helpers/eventFormStateHelper'

const eventsChangedOnAnotherTab = new Set();

export default {
    data() {
        return {
            unModifiedEvent: null,
            activeArea: 'areaDetails',
            isloadingEvent: false,
            hasSameEventUpdatedOnAnotherTab: false,
            isRefreshingEvent: false,
            eventEditChannelHandler: null
        }
    },
    created() {
    },
    mounted() {
        this.setActiveArea('areaDetails');
        const channel = getEventEditChannel();
        if (!channel) return;

        this.eventEditChannelHandler = (e) => {
            const payload = e && e.data ? e.data : null;
            if (!payload.event.id) return;
            if (!this.getSelectedEvent.id) return;

            if (String(payload.event.id) === String(this.getSelectedEvent.id)) {
                eventsChangedOnAnotherTab.add(String(payload.event.id));
                this.hasSameEventUpdatedOnAnotherTab = true;
            }
        };
        channel.addEventListener('message', this.eventEditChannelHandler);

        if (this.getSelectedEvent && eventsChangedOnAnotherTab.has(String(this.getSelectedEvent.id))) {
            this.hasSameEventUpdatedOnAnotherTab = true;
        }

    },
    beforeDestroy() {
        const channel = getEventEditChannel();
        if (channel && this.eventEditChannelHandler) {
            try {
                channel.removeEventListener('message', this.eventEditChannelHandler);
            } catch (err) {}
            this.eventEditChannelHandler = null;
        }
        
    },
    validations: {
        getSelectedEvent: {
            product: {
                isProductValid() {
                    return this.validateProduct;
                }
            },
            location: {
                required
            },
            resources: {
                hasResources() {
                    return this.validateResources;
                }
            },
            end: {
                // Validates event duration
                isValidDuration() {
                    return this.validateDuration;
                }
            },
            rulesBook: {
                // Validate min/max per book
                isValidRulesPerBook() {
                    return this.validateRulesPaxBook;
                }
            },
            rulesEvent: {
                // Validate min lotation per event
                isValidRulesPerEvent() {
                    return this.validateRulesPaxEvent;
                }
            },
            salesChannelsAgent: {
                validateAgentChannel() {
                    return this.validateSalesChannelAgents;
                }
            },
            genderPrices: {
                validateGenderPrices() {
                    return !this.validateCustomGenderPrices;
                }
            }
        }
    },
    props: {
        show: {
            type: Boolean,
            default: false,
        }
    },
    watch: {
        getSelectedEvent: {
            immediate: true,
            handler(event) {
                if (event) this.initEvent(event);
            }
        },
        // If princingType per Order set rules Pax to 0
        'getSelectedEvent.selectedPrincingType': {
            handler(newVal) {
                if (newVal.id == 1) {
                    this.getSelectedEvent.minimumPaxPerBooking = 0;
                    this.getSelectedEvent.maximumPaxPerBooking = 0;
                    this.getSelectedEvent.minimumLotation = 0;
                    this.getSelectedEvent.rejectBookingBasedOnMinMax = false;
                    this.eventActivateUpdateFlag('minMaxRules');
                }
            }
        },
        isResourcesValid: {
            immediate: true,
            handler(val) {
                    this.setHasResourceErrors(!val);
            }
        }
    },
    computed: {
        ...mapGetters([
            'account',
            'getSelectedEvent',
            'isCreatingSingleEvent',
            'getSalesChannelsForNewProduct',
            'getHasDynamicPricingRevenuePlugin'
        ]),
        selectedEventTitle() {
            if (this.isCreatingSingleEvent) return 'Create New Event';
            return this.getSelectedEvent.product.name;
        },
        hasEventChangedOnOtherTab() {
            if (this.isCreatingSingleEvent) return true;
            if (!this.unModifiedEvent) return false;

            const IGNORED_KEYS = new Set([
                'componentRuleType',
            ]);

            const stripUIFields = (key, val) => {
                if (key.endsWith('Helper')) return undefined;
                if (IGNORED_KEYS.has(key)) return undefined;
                return val;
            };

            const normalize = (obj) => JSON.parse(JSON.stringify(obj, stripUIFields));

            return !isEqual(normalize(this.getSelectedEvent), normalize(this.unModifiedEvent));
        },
        ...eventFormStateHelper.mapGetters([
            'isResourcesValid',
            'hasResourceErrors',
            'hasPriceErrors',
            'hasPriceGroupErrors'
        ]),
        tabItems() {
            if (this.isCreatingSingleEvent) {
                return [
                    {
                        label: 'Details',
                        selected: true,
                        area: 'areaDetails',
                        visible: true,
                    },
                    {
                        label: 'Resources / Capacity',
                        selected: false,
                        area: 'areaResources',
                        visible: true,
                    },
                    {
                        label: 'Prices',
                        selected: false,
                        area: 'areaPrices',
                        visible: true,
                    },
                    {
                        label: 'Sales Channels',
                        selected: false,
                        area: 'areaSalesPermissions',
                        visible: true,
                    },
                    {
                        label: 'Booking Question',
                        selected: false,
                        area: 'areaBookingQuestion',
                        visible: true,
                    },
                    {
                        label: 'Cutoff',
                        selected: false,
                        area: 'areaCutoff',
                        visible: this.featureFlags.showCutoff,
                    },
                    {
                        label: 'Min/Max Settings',
                        selected: false,
                        area: 'areaRulesPax',
                        visible: true,
                    },
                    {
                        label: 'Rules',
                        selected: false,
                        area: 'areaRules',
                        visible: this.getHasDynamicPricingRevenuePlugin,
                    },
                    {
                        label: 'Communication',
                        selected: false,
                        area: 'areaCommunication',
                        visible: true,
                    }
                ]
            } else {
                return [
                    {
                        label: 'Details',
                        selected: true,
                        area: 'areaDetails',
                        visible: true,
                    },
                    {
                        label: 'Resources / Capacity',
                        selected: false,
                        area: 'areaResources',
                        visible: true,
                    },
                    {
                        label: 'Prices',
                        selected: false,
                        area: 'areaPrices',
                        visible: true,
                    },
                    {
                        label: 'Sales Channels',
                        selected: false,
                        area: 'areaSalesPermissions',
                        visible: true,
                    },
                    {
                        label: 'Booking Question',
                        selected: false,
                        area: 'areaBookingQuestion',
                        visible: true,
                    },
                    {
                        label: 'Cutoff',
                        selected: false,
                        area: 'areaCutoff',
                        visible: this.featureFlags.showCutoff,
                    },
                    {
                        label: 'Min/Max Settings',
                        selected: false,
                        area: 'areaRulesPax',
                        visible: true,
                    },
                    {
                        label: 'Rules',
                        selected: false,
                        area: 'areaRules',
                        visible: this.getHasDynamicPricingRevenuePlugin,
                    },
                    {
                        label: 'Communication',
                        selected: false,
                        area: 'areaCommunication',
                        visible: true,
                    }
                ]
            }
        },
        validateProduct() {
            if (!this.isCreatingSingleEvent || this.isCreatingSingleEvent == false) {
                return true;
            } else {
                if (!this.getSelectedEvent.helperProduct || this.getSelectedEvent.helperProduct == null) {
                    return false;
                } else {
                    return true;
                }
            }
        },
        validateDuration() {
            let timeDiff = moment(this.getSelectedEvent.end).diff(moment(this.getSelectedEvent.start), 'minutes');
            return timeDiff > 0;
        },
        validateRulesPaxBook() {
            if (this.getSelectedEvent.minimumPaxPerBooking != 0 && this.getSelectedEvent.maximumPaxPerBooking != 0) {
                if (this.getSelectedEvent.minimumPaxPerBooking >= this.getSelectedEvent.maximumPaxPerBooking) {
                    return false;
                } else {
                    return true;
                }
            } else {
                return true;
            }
        },
        validateRulesPaxEvent() {
            if (
                this.getSelectedEvent.minimumLotation != 0 && this.getSelectedEvent.minimumLotation <= this.getSelectedEvent.minimumPaxPerBooking && this.getSelectedEvent.minimumPaxPerBooking != 0 ||
                this.getSelectedEvent.minimumLotation != 0 && this.getSelectedEvent.minimumLotation >= this.getSelectedEvent.maximumPaxPerBooking && this.getSelectedEvent.maximumPaxPerBooking != 0
            ) {
                return false;
            } else {
                return true;
            }
        },
        validateResources() {
            // If availbility is "Limited by Resources" we need to make sure that the event has resources
            if (this.getSelectedEvent.availabilityType == 2) {
                return this.getSelectedEvent.eventResources.length > 0;
            } else {
                return true;
            }
        },
        validateSalesChannelAgents() {
            let agentsChannel = this.getSelectedEvent.channelCommissions.filter(channel => {
                return channel.channelType == 50;
            })

            if (agentsChannel.length > 0) {
                agentsChannel = agentsChannel[0];
            } else {
                return false;
            }

            if (agentsChannel.isActive && this.getSelectedEvent.salesPermissionsAgentsCustom == true && this.getSelectedEvent.salesPermissionsAgentsList.length == 0) {
                return false;
            } else {
                return true;
            }
        },
        validateCustomGenderPrices() {
            return this.getSelectedEvent.eventGenderPrices.some(gp => {
                const audienceType = Number(gp.audienceType);

                return audienceType >= 500 && !gp.audienceTypeAssossiation;
            });
        },
    },
    methods: {
        initEvent(event) {
            if (!event) return;

            if (this.isCreatingSingleEvent) {
                this.backToList();
                this.getSelectedEvent.channelCommissions = this.getSalesChannelsForNewProduct;
                this.setHasPriceErrors(true);
            }

            if (!this.isCreatingSingleEvent && this.hasSameEventUpdatedOnAnotherTab && !this.isRefreshingEvent && this.unModifiedEvent && String(event.id) === String(this.unModifiedEvent.id)) {
                try {
                    const snapshot = structuredClone(this.unModifiedEvent);
                    Object.assign(this.getSelectedEvent, snapshot);
                } catch (err) {}
            } else if (!this.isCreatingSingleEvent) {
                this.unModifiedEvent = structuredClone(this.getSelectedEvent);
            }

            // Set custom agents flag
            if (this.getSelectedEvent.salesPermissionsAgentsList && this.getSelectedEvent.salesPermissionsAgentsList.length > 0) {
                this.$set(this.getSelectedEvent, 'salesPermissionsAgentsCustom', true);
            } else {
                this.$set(this.getSelectedEvent, 'salesPermissionsAgentsCustom', false);
            }
        },
        ...mapActions([
            'openModal',
            'closeModal',
            'cancelEventEdit',
            'resetBasePrices',
            'setBasePrices',
            'updateSelectedEvent',
            'downloadSingleEvent',
            'downloadGenderPricePerChannel',
            'initEventNotes'
        ]),
        ...eventFormStateHelper.mapActions([
            'setHasResourceErrors',
            'setHasPriceErrors',
            'setHasPriceGroupErrors'
        ]),
        async reloadSelectedEvent() {
            if (!this.getSelectedEvent || !this.getSelectedEvent.id) return;
                this.isloadingEvent = true;
                this.isRefreshingEvent = true;
                this.hasSameEventUpdatedOnAnotherTab = false;
                eventsChangedOnAnotherTab.delete(String(this.getSelectedEvent.id));

            try {
                const response = await this.downloadSingleEvent(this.getSelectedEvent.id);
                const freshEvent = response && response.data ? response.data : null;

                if (!freshEvent || !freshEvent.id) {
                    return;
                }
                
                await this.updateSelectedEvent(freshEvent.id);
                this.setBasePrices(freshEvent);
                await this.downloadGenderPricePerChannel(freshEvent.id);
                await this.initEventNotes(freshEvent.id);
                this.unModifiedEvent = structuredClone(this.getSelectedEvent);

                eventHub.$emit('updateCalendar');
            } catch (err) {
            } finally {
                this.isloadingEvent = false;
                this.isRefreshingEvent = false;
            }
        },
        backToList() {
            // Back to calendar filters list
            $('.events .list').removeClass('moved-left');
            $('.events .more').addClass('moved-right');
        },
        selectTabItem(tabItem) {
            for (let tab of this.tabItems) {
                tab.selected = false;
            }
            tabItem.selected = true;
            this.activeArea = tabItem.area;
        },
        setActiveArea(areaName) {
            for (let tab of this.tabItems) {
                if (tab.area == areaName) {
                    tab.selected = true;
                    this.activeArea = areaName;
                } else {
                    tab.selected = false;
                }
            }
        },
        hasTabError(tabItem) {
            if (!tabItem || !tabItem.area) return false;

            const checks = {
                areaDetails: () => (!this.validateProduct || !this.validateDuration || !(this.getSelectedEvent && this.getSelectedEvent.location)),
                areaResources: () => this.hasResourceErrors,
                areaPrices: () => (this.hasPriceErrors || this.hasPriceGroupErrors || this.validateCustomGenderPrices),
            };

            const checker = checks[tabItem.area];
            return checker ? checker() : false;
        },
        cancelEdit() {
            this.cancelEventEdit(this.unModifiedEvent);

            if (this.isCreatingSingleEvent) {
                this.backToList();
                this.updateSelectedEvent(null);
            }
            
            this.setHasPriceErrors(false);
            this.setHasPriceGroupErrors(false);
            this.resetBasePrices();
            if (this.$route.name === 'calendarExpandedWithEventIdEdit') {
                const { currentView, expandedDay, eventID } = this.$route.params || {};
                this.$router.push({
                    name: 'calendarExpandedWithEventId',
                    params: {
                        currentView: currentView,
                        expandedDay: expandedDay,
                        eventID: eventID
                    }
                });
            }

            this.closeModal();
        }
    },
    components: {
        Modal,
        footerButtons,
        areaDetails,
        areaCommunication,
        areaCapacity,
        areaResources,
        areaRulesPax,
        areaSalesPermissions,
        areaBookingQuestion,
        areaPrices,
        areaTransports,
        areaCutoff,
        areaRules,
        Loader
    }
}
</script>

<style lang="scss">
// Not scopped so it affects modal-body, parent eventEdit-modal used instead of scooped
.eventEdit-modal {
    .overflowAuto {
        overflow: auto;
    }
}
</style>

<style lang="scss" scoped>
.eventEditBody {
    min-height: 460px;
}

.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #fff;
    z-index: 5;
}

.overflow-test {
    height: 70.9vh;
    overflow: auto;
}

.tab-error-dot {
    width: 8px;
    height: 8px;
    background: #d64545;
    border-radius: 50%;
    display: inline-block;
    margin-left: 8px;
}

.event-changed-banner {
    max-height: 0;
    padding: 0;
    transition: all ease-in-out 400ms;
    height: 0;
    overflow: hidden;

    &.active {
        max-height: none;
        padding: 0.25rem 0;
        height: 1.9rem;
    }
}
</style>



// WEBPACK FOOTER //
// EventEdit.vue?68725a8e